// ? This card should match the PostCard in Thread/PostCard as much as possible
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import {
  mdiAlertCircleCheckOutline,
  mdiAlertCircleOutline,
  mdiThumbUp,
  mdiThumbUpOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  ContentCopyOutlined,
  DeleteForeverOutlined,
  EditOutlined,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { getContentUrl, useFollowUnfollowForumTopic } from '@juno/client-api';
import {
  CommunityUser,
  ForumTopic,
  JunoUser,
  SearchContentTypeEnum,
  Tag,
} from '@juno/client-api/model';
import { ANALYTICS_CONFIGURATION } from '@juno/constants';
import { UserQuickView, customShadow } from '@juno/ui';
import {
  MutationAction,
  getContentRoute,
  getTimeSinceCreated,
  onMutation,
  snackOptions,
  useSettings,
} from '@juno/utils';
import { useAnalyticsContext } from '../Contexts/AnalyticsContext';
import CollapsibleMenu from '../Thread/CollapsibleMenu';
import LittleHeads from '../Thread/LittleHeads';
import { TimeSinceCreatedText } from '../Thread/styles';

const MAX_CONTENT_HEIGHT = 200;

interface ForumTopicCardProps {
  topic: ForumTopic;
  me: JunoUser | undefined;
  refetchMe: () => void;
  refetchTopics: () => void;
  topicsFetching: boolean;
  siteId: string;
  handleEditTopic: (topic: ForumTopic) => void;
  setDeleteDialogOpen: (value: React.SetStateAction<boolean>) => void;
  setToDeleteTopicId: Dispatch<SetStateAction<string | null>>;
  handlePinUnpin: (topic: ForumTopic) => Promise<void>;
  iAmAdmin?: boolean;
  handleTagFilter?: (tag: Tag) => void;
  userCanInteract?: boolean;
}

const ForumTopicCard: React.FC<ForumTopicCardProps> = ({
  topic,
  me,
  refetchMe,
  siteId,
  refetchTopics,
  topicsFetching,
  handleEditTopic,
  setDeleteDialogOpen,
  setToDeleteTopicId,
  handlePinUnpin,
  iAmAdmin = false,
  handleTagFilter,
  userCanInteract = true,
}) => {
  const { setReportContent, reportContent, myContentReports, site } = useSettings();
  const { firehoseActivity } = useAnalyticsContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [postHeight, setPostHeight] = useState<string | number>(MAX_CONTENT_HEIGHT);
  const contentRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const numFollowers = topic.followers_count;
  const numPosts = topic.feed.post_count || 0;
  // TODO - this exists cuz it was easy to add and was a POC. Prod chose to remove it atm, but leaving here for now since it may be useful in the future
  // const topPostAuthors = topic.feed.top_post_authors || [];
  const amFollowing = !!me?.followed_forum_topics.find((t: string) => t === topic.id);
  const myPost = topic.author && topic.author?.id === me?.id;

  const refetch = () => {
    refetchMe();
    refetchTopics();
  };

  const followUnfollow = useFollowUnfollowForumTopic(
    onMutation(MutationAction.UPDATE, '', refetch),
  );

  const handleFollowUnfollow = async () => {
    try {
      await followUnfollow.mutateAsync({
        siteId: siteId,
        forumId: topic.forum_id,
        topicId: topic.id,
        userId: me?.id || '',
      });
      const action = amFollowing
        ? ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_UNFOLLOW.value
        : ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_FOLLOW.value;
      firehoseActivity(
        ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
        topic.id || '',
        topic.forum_id,
        `${me?.id || ''}`,
        ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM_TOPIC,
        action,
        topic.title,
        null,
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleCopyLink = async () => {
    if (!navigator.clipboard) {
      console.error('Clipboard API not supported');
      return;
    }

    const urlResponse = await getContentUrl(
      siteId,
      topic.id,
      location.pathname.split('/').pop() || '',
    );
    const locationSplit = window.location.href.split('/');
    locationSplit.pop();
    const locationHref = locationSplit.join('/');
    const contentUrl = `${locationHref}${urlResponse.url}`;

    navigator.clipboard
      .writeText(contentUrl)
      .then(() => {
        enqueueSnackbar('Copied!', snackOptions('success'));
      })
      .catch((error) => {
        enqueueSnackbar('Copy Failed!', snackOptions('error'));
        console.error('Failed to copy URL to clipboard:', error);
      });
  };

  const handleDelete = () => {
    if (myPost) {
      setDeleteDialogOpen(true);
      setToDeleteTopicId(topic.id);
    } else {
      setReportContent({
        contentBeingReported: {
          ...reportContent.contentBeingReported,
          id: topic.id,
          type: 'Discussion',
          reporter: me?.id || '',
          author: topic.author?.id || '',
        },
        adminDelete: true,
        reportContentDialogOpen: true,
      });
    }
  };

  const htmlValue = useMemo(() => {
    const sanitizedString = DOMPurify.sanitize(topic?.description || ('' as string), {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
    });
    // uncomment to debug what's getting removed
    // console.log('Dirty values', DOMPurify.removed);
    return sanitizedString;
  }, [topic.description]);

  useEffect(() => {
    const container = contentRef.current;
    const content = container?.querySelector('.content') as HTMLElement | null;

    if (container && content) {
      setPostHeight(content.scrollHeight);
    }
  }, [isExpanded, topic.description]);

  const [userQuickViewAnchorEl, setUserQuickViewAnchorEl] = useState<null | HTMLElement>(null);
  const [userQuickViewUserId, setUserQuickViewUserId] = useState<string>('');
  const quickViewRef = useRef(null);
  const navigateRoute = useNavigate();

  return (
    <Card
      sx={{
        p: 2,
        '&:hover': { boxShadow: userCanInteract ? 3 : 0 },
        cursor: userCanInteract ? 'pointer' : 'default',
      }}
      onClick={() => navigateRoute(`${location.pathname}/${topic.id}`)}
    >
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mt={'5px'}
        mb={2}
      >
        <Box
          display={'flex'}
          justifyContent={'flex-start'}
          alignItems={'center'}
          position={'relative'}
        >
          {topic.author?.active ? (
            <>
              <Avatar
                src={topic.author?.icon || topic.author?.avatar || ''}
                alt={`${topic.author?.first_name} ${topic.author?.last_name}`}
                ref={quickViewRef}
                style={{ cursor: 'pointer', width: 50, height: 50 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setUserQuickViewAnchorEl(quickViewRef.current);
                  setUserQuickViewUserId(topic.author?.id || '');
                }}
              />
              <Stack
                spacing={0}
                ml={2}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setUserQuickViewAnchorEl(quickViewRef.current);
                  setUserQuickViewUserId(topic.author?.id || '');
                }}
              >
                <Typography
                  variant='subtitle2'
                  fontWeight={'bold'}
                  style={{ cursor: 'pointer', fontSize: '16px' }}
                >{`${topic.author?.first_name} ${topic.author?.last_name}`}</Typography>
              </Stack>
            </>
          ) : (
            <Tooltip title='Deactivated User' placement='top-start'>
              <span
                style={{
                  display: 'flex',
                  opacity: '0.3',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'not-allowed',
                }}
              >
                <Avatar
                  src={topic.author?.icon || topic.author?.avatar || ''}
                  alt={`${topic.author?.first_name} ${topic.author?.last_name}`}
                  ref={quickViewRef}
                  style={{ width: 50, height: 50 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                />
                <Stack
                  spacing={0}
                  ml={2}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight={'bold'}
                    style={{ fontSize: '16px' }}
                  >{`${topic.author?.first_name} ${topic.author?.last_name}`}</Typography>
                </Stack>
              </span>
            </Tooltip>
          )}
        </Box>
        <TimeSinceCreatedText
          variant='subtitle2'
          fontWeight={'normal'}
          fontSize={'12px'}
          onClick={(e) => {
            if (userCanInteract) {
              navigateRoute(topic.id);
            }
          }}
        >
          {getTimeSinceCreated(false, topic.date_created)}
        </TimeSinceCreatedText>
      </Box>
      <UserQuickView
        anchorEl={userQuickViewAnchorEl}
        userId={userQuickViewUserId}
        open={Boolean(userQuickViewAnchorEl)}
        setAnchorEl={setUserQuickViewAnchorEl}
        onViewProfile={(userId: string): void => {
          navigateRoute(getContentRoute(site?.id || '', userId, SearchContentTypeEnum.user));
        }}
        onMessageUser={function (userId: string): void {
          throw new Error('Function not implemented.');
        }}
      />
      <Box
        position={'relative'}
        display={'flex'}
        justifyContent={'flex-start'}
        alignItems={'center'}
      >
        <Typography
          variant='h6'
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 40px)',
          }}
        >
          {topic.title}
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            right: 0,
          }}
        >
          <CollapsibleMenu maxVisibleIcons={0}>
            {iAmAdmin && (
              <Tooltip title={topic.pinned ? 'Unpin' : 'Pin'} arrow>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handlePinUnpin(topic);
                  }}
                >
                  {topic.pinned ? (
                    <PushPin
                      sx={{
                        transform: 'rotate(45deg)',
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />
                  ) : (
                    <PushPinOutlined sx={{ transform: 'rotate(45deg)' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={amFollowing ? 'Unfollow' : 'Follow'} arrow>
              <span>
                <IconButton
                  disabled={!userCanInteract}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleFollowUnfollow();
                  }}
                >
                  {amFollowing ? (
                    <Icon path={mdiThumbUp} size={1} color={theme.palette.primary.main} />
                  ) : (
                    <Icon path={mdiThumbUpOutline} size={1} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Copy Link' arrow>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  handleCopyLink();
                }}
              >
                <ContentCopyOutlined />
              </IconButton>
            </Tooltip>
            {myPost && (
              <Tooltip title='Edit' arrow>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleEditTopic(topic);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                myContentReports.find((r) => r.object_id === topic.id)
                  ? 'Already Reported'
                  : 'Report Discussion'
              }
              arrow
            >
              <span>
                <IconButton
                  disabled={!!myContentReports.find((r) => r.object_id === topic.id)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    setReportContent({
                      contentBeingReported: {
                        ...reportContent.contentBeingReported,
                        id: topic.id,
                        type: 'Discussion',
                        reporter: me?.id || '',
                        author: topic.author?.id || '',
                      },
                      reportContentDialogOpen: true,
                    });
                  }}
                >
                  {myContentReports.find((r) => r.object_id === topic.id) ? (
                    <Icon path={mdiAlertCircleCheckOutline} size={1} />
                  ) : (
                    <Icon path={mdiAlertCircleOutline} size={1} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {myPost ? (
              <Tooltip title='Delete' arrow>
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleDelete();
                  }}
                >
                  <DeleteForeverOutlined />
                </IconButton>
              </Tooltip>
            ) : (
              iAmAdmin && (
                <Tooltip title='Delete as Admin' arrow>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      handleDelete();
                    }}
                  >
                    <DeleteForeverOutlined />
                  </IconButton>
                </Tooltip>
              )
            )}
          </CollapsibleMenu>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          mt: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          backgroundColor: '#f4f4f4',
        }}
      >
        <Box
          maxHeight={
            isExpanded
              ? 'auto'
              : topic.tags && topic.tags?.length > 0
              ? MAX_CONTENT_HEIGHT + 32
              : MAX_CONTENT_HEIGHT
          }
          overflow={'hidden'}
          textOverflow={'ellipsis'}
          ref={contentRef}
        >
          <div
            className='content'
            dangerouslySetInnerHTML={{
              __html: htmlValue as unknown as string,
            }}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              const link = target.closest('a');

              if (link) {
                e.stopPropagation();
                return;
              }
            }}
          ></div>
          {topic.tags && topic.tags?.length > 0 && (
            <Box pb={1}>
              {topic.tags.map((t) => (
                <Tooltip key={t.id} title={`Filter by "${t.value}"`} arrow>
                  <Chip
                    key={t.id}
                    label={t.value}
                    sx={{
                      ml: 1,
                      p: '2px 6px',
                      boxShadow: customShadow,
                      color: 'inherit',
                      bgcolor: 'inherit',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleTagFilter && handleTagFilter(t)} // TODO
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mt={1}>
        <Grid container>
          <Grid item sm={3} xs={12}>
            {(typeof postHeight === 'string' || postHeight >= MAX_CONTENT_HEIGHT) &&
              !topicsFetching && (
                <Button
                  size='small'
                  variant='text'
                  aria-label={isExpanded ? 'See Less' : 'See More'}
                  color='primary'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    setIsExpanded((prevExpanded) => !prevExpanded);
                  }}
                >
                  {isExpanded ? 'See Less' : 'See More'}
                </Button>
              )}
          </Grid>
          <Grid item sm={9} xs={12}>
            <Grid container display={'flex'} justifyContent={'center'} alignItems={'center'}>
              <Grid item xs={3} sx={{ display: { md: 'block', xs: 'none' } }}></Grid>
              <Grid
                item
                md={3}
                sm={4}
                xs={12}
                display={'flex'}
                justifyContent={{ sm: 'center', xs: 'right' }}
              >
                {topic.tags && topic.tags?.length > 0 && (
                  <Button disabled>
                    <LittleHeads items={[topic.tags?.length]} />
                    <Typography
                      fontSize={14}
                      ml={1}
                      sx={{
                        color:
                          topic.tags?.length > 0 ? (theme) => theme.palette.primary.main : 'black',
                      }}
                    >
                      {topic.tags?.length == 1 ? 'Tag' : 'Tags'}
                    </Typography>
                  </Button>
                )}
              </Grid>
              <Grid
                item
                md={3}
                sm={4}
                xs={12}
                display={'flex'}
                justifyContent={{ sm: 'center', xs: 'right' }}
              >
                <Button disabled={!userCanInteract}>
                  <LittleHeads items={[numPosts]} totalItems={numPosts} />
                  <Typography
                    fontSize={14}
                    ml={1}
                    sx={{
                      color: numPosts > 0 ? (theme) => theme.palette.primary.main : 'black',
                    }}
                  >
                    {numPosts == 1 ? 'Post' : 'Posts'}
                  </Typography>
                </Button>
              </Grid>
              <Grid
                item
                md={3}
                sm={4}
                xs={12}
                display={'flex'}
                justifyContent={{ sm: 'center', xs: 'right' }}
              >
                <Button disabled>
                  <LittleHeads
                    items={(topic.top_followers as CommunityUser[]) || 0}
                    totalItems={numFollowers}
                  />
                  <Typography
                    fontSize={14}
                    ml={1}
                    sx={{
                      color: numFollowers > 0 ? (theme) => theme.palette.primary.main : 'black',
                    }}
                  >
                    {numFollowers == 1 ? 'Follower' : 'Followers'}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};
export default ForumTopicCard;
